import React, { useEffect, useState } from 'react';
import {
  Box, Modal, Tabs, Tab, TextField, Button, Typography,
  Select, MenuItem, AppBar, Toolbar, Stack, FormControlLabel, Switch, Collapse, Paper
} from '@mui/material';
import {
  collection, getDocs, orderBy, query, doc, updateDoc,
  deleteDoc, getFirestore, setDoc, where, serverTimestamp
} from 'firebase/firestore';
import { db, auth,  adminDeleteAccount } from '../firebase';
import { initializeApp, getApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";


/* ---------------------- Plan helpers (package-aware) ---------------------- */


/* ---------------------- Manual plan catalog (admin-created users) ---------------------- */

const MANUAL_PLAN_CATALOG = {
  free: {
    label: "Free Plan",
    displayName: "Free Plan",
    monthlyQuota: 5,
  },
  personal: {
    label: "Personal Plan",
    displayName: "Personal Plan",
    monthlyQuota: 50,
  },
  // ✅ family has two variants
  family_lite: {
  label: "Family Lite Plan",
  displayName: "Family Lite Plan",
  monthlyQuota: 80,   // ✅ Family Lite = 80
},
family: {
  label: "Big Family Plan",
  displayName: "Big Family Plan",
  monthlyQuota: 150,  // ✅ Big Family = 150
},

};

const toManualPlanKey = (planValue) => {
  const v = (planValue || "").toLowerCase().trim();

  if (v.includes("family lite") || v.includes("family_lite") || v === "family-lite") return "family_lite";
  if (v.includes("family")) return "family";
  if (v.includes("personal")) return "personal";
  return "free";
};

const manualPlanDisplay = (planKey) =>
  MANUAL_PLAN_CATALOG[planKey]?.displayName || "Free Plan";


const normalizePlan = (p) => (p || '').toLowerCase().replace(/\s+/g, ' ').trim();

const readKind = (u) =>
  (u?.subscription?.packageKind ??
   u?.stripe?.packageKind ??
   '').toLowerCase();

const readKey = (u) =>
  (u?.subscription?.packageKey ??
   u?.stripe?.packageKey ??
   '').toLowerCase();

const isInvoiceSchool = (u) =>
  u?.subscription?.source === "invoice" || u?.subscription?.provider === "offline";


/** Bucket a user into: 'school' | 'family' | 'personal' | 'free' | 'student' | 'other' */
const deriveBucketRaw = (u) => {
  const role = (u?.role || '').toLowerCase();
  const kind = readKind(u);
  const key  = readKey(u);
  const plan = normalizePlan(u?.plan);

  // NEW: roles
  if (role === 'student') return 'student';
  if (role === 'teacher') return 'school'; // ✅ counts as a school account

  if (
  role === 'school' ||
  kind === 'school' ||
  key.includes('school') ||
  isInvoiceSchool(u)
) return 'school';

  if (
  kind.includes('family') ||
  key.includes('family') ||
  plan.includes('family') ||
  key.includes('family_lite') ||
  plan.includes('family lite')
) return 'family';

  if (kind.includes('personal') || key.includes('personal') || plan.includes('personal')) return 'personal';

  if (kind.includes('free') || key.includes('free') || plan === 'free plan') return 'free';
  if (kind.includes('starter') || key.includes('starter') || plan.includes('starter')) return 'free';
  if (kind.includes('trial') || key.includes('trial')) return 'free';

  return 'other';
};

const subStatusOf = (u) => {
  const s = u?.subscription || u?.stripe || null;
  const status = (s?.status || "").toLowerCase();

  if (!s) return "—";
  if (status) return status;

  // fallback heuristics
  if (s?.currentPeriodEnd) return "active";
  return "—";
};

const cancelFlagOf = (u) => {
  const s = u?.subscription || u?.stripe || null;
  return !!(s?.cancelAtPeriodEnd || s?.cancel_at_period_end);
};

const providerOf = (u) => {
  const s = u?.subscription || u?.stripe || null;

  // invoice/offline
  if (u?.subscription?.source === "invoice" || u?.subscription?.provider === "offline") return "invoice";

  // manual (admin created)
  if (u?.subscription?.source === "manual" || u?.subscription?.provider === "manual") return "manual";

  // stripe (default if stripe/subscription object exists)
  if (s) return "stripe";

  return "—";
};




/** Normalize bucket so users with no plan/subscription are treated as FREE,
 *  EXCEPT students (keep them as 'student') so they don't leak into Free.
 */
const deriveBucket = (u) => {
  const raw = deriveBucketRaw(u);
  if (raw !== 'other') return raw;

  // If truly unknown and not student, treat as free
  const role = (u?.role || '').toLowerCase();
  if (role === 'student') return 'student';

  const hasSubMeta = !!(u?.subscription || u?.stripe);
  const hasNamedPlan = !!(u?.plan && normalizePlan(u.plan) !== '');
  if (!hasSubMeta && !hasNamedPlan) return 'free';
  return 'other';
};


/** Display name preference */
const planDisplayOf = (u) => {
  const display =
    u?.subscription?.displayName ||
    u?.stripe?.displayName ||
    u?.plan ||
    (deriveBucket(u) === "free" ? "Free Plan" : "—");

 const key = readKey(u);

// ✅ if key is family_lite, force Family Lite label
if (key === "family_lite" && (display || "").toLowerCase().includes("family")) {
  return "Family Lite Plan";
}

// ✅ if key is family (big family), force Big Family label
if (key === "family" && (display || "").toLowerCase().includes("family")) {
  return "Big Family Plan";
}

return display;

};



/* ---------------------- School label helpers (old + new) ---------------------- */

const pick = (...vals) => vals.find(v => v != null && String(v).trim() !== "") ?? null;

function buildSchoolMap(schoolsRaw, usersRaw) {
  const userById = Object.fromEntries((usersRaw || []).map(u => [u.id, u]));
  const map = {};

  const pick = (...vals) => vals.find(v => v != null && String(v).trim() !== "") ?? null;

  const labelFor = (s) => {
    if (!s) return null;

    // Prefer users/{schoolId} doc name fields
    const schoolUser = userById?.[s.id];
    const fromSchoolUser = pick(
      schoolUser?.name,
      schoolUser?.fullName,
      schoolUser?.schoolName
    );
    if (fromSchoolUser) return fromSchoolUser;

    // Legacy explicit fields on schools/{id}
    const explicitName = pick(
      s.name,
      s.schoolName,
      s.displayName,
      s.profile?.name,
      s.data?.name
    );
    if (explicitName) return explicitName;

    // Minimal schema → derive from ownerId’s user
    if (s.ownerId) {
      const owner = userById?.[s.ownerId];
      const ownerLabel = pick(owner?.fullName, owner?.name, owner?.email, s.ownerId);
      if (ownerLabel) return `School of ${ownerLabel}`;
    }

    // Fallback
    return `School ${String(s.id || "").slice(0, 6)}`;
  };

  (schoolsRaw || []).forEach(s => {
    map[s.id] = labelFor({ id: s.id, ...s });
  });

  return { schoolMap: map, userById };
}


/* ---------------------- Date helpers for “Last Active” ---------------------- */

function parseYmd(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return null;
  const d = new Date(dateStr);
  return isNaN(d) ? null : d;
}

function latestReadingActivityDate(activityArr) {
  if (!Array.isArray(activityArr) || activityArr.length === 0) return null;
  let latest = null;
  for (const e of activityArr) {
    const d = parseYmd(e?.date);
    if (d && (!latest || d > latest)) latest = d;
  }
  return latest;
}


const formatUkDate = (dateLike) => {
  if (!dateLike) return "—";
  let d = null;

  if (dateLike?.toDate) {
    d = dateLike.toDate(); // Firestore Timestamp
  } else if (dateLike instanceof Date) {
    d = dateLike;
  } else if (typeof dateLike === "number") {
    d = new Date(dateLike > 1e12 ? dateLike : dateLike * 1000);
  } else if (typeof dateLike === "string") {
    d = new Date(dateLike);
  }

  if (!d || isNaN(d.getTime())) return "—";

  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};



/* ---------------------- Invoice plan catalogue ---------------------- */

const INVOICE_PLAN_CATALOG = {
  school_50:  { displayName: "School Plan (50 seats)",  seats: 50,  amount: 3234.60,  billingInterval: "annual", currency: "GBP" },
  school_100: { displayName: "School Plan (100 seats)", seats: 100, amount: 5929.20, billingInterval: "annual", currency: "GBP" },
  school_200: { displayName: "School Plan (200 seats)", seats: 200, amount: 10778.40, billingInterval: "annual", currency: "GBP" },

  // 250+ special: leave seats/amount blank until you set a deal
  school_250_plus: { displayName: "School Plan (250+ seats)", seats: "", amount: "", billingInterval: "annual", currency: "GBP" },
};

const getPlanDefaults = (packageKey) => INVOICE_PLAN_CATALOG[packageKey] || null;



const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [messages, setMessages] = useState([]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [editTarget, setEditTarget] = useState(null);

  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminRole, setNewAdminRole] = useState('admin');

  // Usage tab state
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [usageUsers, setUsageUsers] = useState([]);       // normalized users
  const [usageStudents, setUsageStudents] = useState([]); // normalized students
  const [usageSummary, setUsageSummary] = useState(null);
  const [schoolOptions, setSchoolOptions] = useState([]); // [{id, name}]
  const [schoolFilter, setSchoolFilter] = useState("All");
  const [invoiceRequests, setInvoiceRequests] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState("all"); // all | pending | contacted | invoiced | paid | activated

  // Deal/Invoice modal state
const [dealModalOpen, setDealModalOpen] = useState(false);
const [dealLoading, setDealLoading] = useState(false);
const [dealTarget, setDealTarget] = useState(null); // the invoiceRequests row
const [dealForm, setDealForm] = useState({
  packageKey: "school_100",
  displayName: "",
  billingInterval: "annual",
  currency: "GBP",
  seats: "",
  amount: "",
  notes: "",
});



  const [allSchools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [schoolModalOpen, setSchoolModalOpen] = useState(false);
  const [schoolSubTab, setSchoolSubTab] = useState(0);

  const [schoolEditModalOpen, setSchoolEditModalOpen] = useState(false);
  const [schoolEditData, setSchoolEditData] = useState(null);

  const [messageFilter, setMessageFilter] = useState("all"); // 👈 add this

  const [newUserModalOpen, setNewUserModalOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: '',
    fullName: '',
    country: '',
    plan: 'Free Plan',
    renewalDate: '',
    password: ''
  });

  const [newSchoolModalOpen, setNewSchoolModalOpen] = useState(false);
  const [newSchoolData, setNewSchoolData] = useState({
  schoolName: '',
  email: '',
  contactName: '',
  country: '',
  phoneNumber: '',
  renewalDate: '',

  // ✅ NEW: invoice-style plan fields (for silent activation)
  packageKey: 'school_100',
  billingInterval: 'annual',
  seats: 100,

  password: ''
});


  const [planTab, setPlanTab] = useState(0); // 0=All, 1=Free, 2=Personal, 3=Family
  const [includeSchools, setIncludeSchools] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  // A separate named Firebase app to create users without swapping the admin session
  const getProvisioningAuth = () => {
    const mainApp = getApp();
    const config = mainApp.options;
    const secondary =
      getApps().find(a => a.name === 'provisioning') ||
      initializeApp(config, 'provisioning');
    return getAuth(secondary);
  };

  /* ---------------------- Usage stats (package-aware + lifetime) ---------------------- */

  const fetchUsageStats = async () => {
    setLoadingUsage(true);
    try {
      const [usersSnap, studentsSnap, schoolsSnap] = await Promise.all([
        getDocs(collection(db, "users")),
        getDocs(collection(db, "students")),
        getDocs(collection(db, "schools")),
      ]);

      const usersRaw    = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
const studentsRaw = studentsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
const schoolsRaw  = schoolsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

// Build labels (preserves old names and supports new ownerId-only docs)
const { schoolMap } = buildSchoolMap(schoolsRaw, usersRaw);

// Any school IDs that exist only via students/orgId should still appear
const idsFromStudents = new Set(
  (studentsRaw || [])
    .map(s => s.schoolId || s.orgId)
    .filter(Boolean)
);

// Merge into options
const optionsById = new Map(
  (schoolsRaw || []).map(s => [s.id, { id: s.id, name: schoolMap[s.id] }])
);
idsFromStudents.forEach(id => {
  if (!optionsById.has(id)) {
    optionsById.set(id, { id, name: `School ${String(id).slice(0, 6)}` });
  }
});

setSchoolOptions(
  Array.from(optionsById.values()).sort((a, b) => a.name.localeCompare(b.name))
);



      // USERS (exclude admins), compute lifetime + better lastActive
      const normalizedUsers = usersRaw
        .filter(u => {
    const role = (u.role || "").toLowerCase();
    return role !== "admin" && role !== "student"; // NEW: exclude student user docs
  })
        .map(u => {
          const activity = Array.isArray(u.readingActivity) ? u.readingActivity : [];
          const activitySum = activity.reduce((sum, e) => sum + (e?.storiesGenerated || 0), 0);
          const storiesLifetime =
            (u.stats?.storiesLifetime ?? null) != null
              ? Number(u.stats.storiesLifetime || 0)
              : (u.storiesRead ?? u.storiesGenerated ?? activitySum ?? 0);

          const la =
            (u.lastActive?.toDate ? u.lastActive.toDate() : (u.lastActive ? new Date(u.lastActive) : null)) ||
            (u.lastRead?.toDate ? u.lastRead.toDate() : (u.lastRead ? new Date(u.lastRead) : null)) ||
            latestReadingActivityDate(activity) ||
            (u.createdAt?.toDate ? u.createdAt.toDate() : (u.createdAt ? new Date(u.createdAt) : null)) ||
            (u.planUpdatedAt?.toDate ? u.planUpdatedAt.toDate() : (u.planUpdatedAt ? new Date(u.planUpdatedAt) : null));

          return {
            id: u.id,
            fullName: u.fullName || u.name || "—",
            email: u.email || "—",
            plan: planDisplayOf(u),
            bucket: deriveBucket(u),
            storiesLifetime,
            ttsPlaybacks: u.ttsPlaybacks ?? 0,
            totalReadingTime: Math.round(u.totalReadingTime ?? 0),
            lastActive: la || null,
            role: u.role || "user",
          };
        });

      // STUDENTS
      const normalizedStudents = studentsRaw.map(s => {
        const activity = Array.isArray(s.readingActivity) ? s.readingActivity : [];
        const activitySum = activity.reduce((sum, e) => sum + (e?.storiesGenerated || 0), 0);
        const storiesLifetime = (s.storiesRead ?? activitySum ?? 0);

        const la =
          (s.lastRead?.toDate ? s.lastRead.toDate() : (s.lastRead ? new Date(s.lastRead) : null)) ||
          latestReadingActivityDate(activity) ||
          (s.lastActive?.toDate ? s.lastActive.toDate() : (s.lastActive ? new Date(s.lastActive) : null)) ||
          (s.createdAt?.toDate ? s.createdAt.toDate() : (s.createdAt ? new Date(s.createdAt) : null));

        return {
          id: s.id,
          fullName: `${s.firstName || ""} ${s.lastName || ""}`.trim() || "—",
          email: s.username
            ? (s.username.includes("@") ? s.username : `${s.username}@yourstoryworld.com`)
            : "—",
          plan: "Student",
          storiesLifetime,
          ttsPlaybacks: s.ttsPlaybacks ?? 0,
          totalReadingTime: Math.round((s.totalReadingTime ?? 0) / 60),
          lastActive: la || null,
          role: "student",
          schoolId: s.schoolId || s.orgId || null,
          schoolName:
  (s.schoolId && (schoolMap[s.schoolId] || `School ${String(s.schoolId).slice(0, 6)}`)) ||
  (s.orgId    && (schoolMap[s.orgId]    || `School ${String(s.orgId).slice(0, 6)}`)) ||
  "—",
        };
      });

      setUsageUsers(normalizedUsers);
      setUsageStudents(normalizedStudents);
      
      // Summaries (use lifetime)
      const summarizeUsers = (arr) => {
        let stories = 0, tts = 0;
        arr.forEach(u => {
          stories += u.storiesLifetime || 0;
          tts += u.ttsPlaybacks || 0;
        });
        return { count: arr.length, stories, tts };
      };

      const publicUsers = normalizedUsers.filter(u => ['free','personal','family','other'].includes(u.bucket));
      const schoolUsers  = normalizedUsers.filter(u => u.bucket === 'school');

      const publicStats  = summarizeUsers(publicUsers);
      const schoolStats  = summarizeUsers(schoolUsers);

      // students summary
      const summarizeStudents = (arr) => {
        let stories = 0, tts = 0;
        arr.forEach(s => {
          stories += s.storiesLifetime || 0;
          tts += s.ttsPlaybacks || 0;
        });
        return { count: arr.length, stories, tts };
      };
      const studentStats = summarizeStudents(normalizedStudents);

      // very rough cost model (adjust if you like)
      const costPerStory = 0.002;
      const costPerImage = 0.01;
      const costPerTTS   = 0.002;
      const calcCost = ({ stories, tts }) =>
        (stories * (costPerStory + costPerImage)) + (tts * costPerTTS);

      // per-school breakdown from students
      const studentsBySchool = {};
      normalizedStudents.forEach(s => {
        const sid = s.schoolId || "_none";
        if (!studentsBySchool[sid]) studentsBySchool[sid] = [];
        studentsBySchool[sid].push(s);
      });

      const schoolUsage = Object.entries(studentsBySchool).map(([sid, group]) => {
        const name = sid === "_none"  ? "—"  : (schoolMap[sid] || `School ${String(sid).slice(0, 6)}`);
        const stories = group.reduce((sum, x) => sum + (x.storiesLifetime || 0), 0);
        const tts     = group.reduce((sum, x) => sum + (x.ttsPlaybacks || 0), 0);
        const cost    = calcCost({ stories, tts });
        return {
          schoolId: sid,
          name,
          studentCount: group.length,
          stories,
          tts,
          cost: cost.toFixed(2),
        };
      }).sort((a,b) => b.stories - a.stories);

      setUsageSummary({
        public:  { ...publicStats,  cost: calcCost(publicStats).toFixed(2) },
        schools: { ...schoolStats,  cost: calcCost(schoolStats).toFixed(2) },
        students:{ ...studentStats, cost: calcCost(studentStats).toFixed(2) },
        total: {
          users: publicStats.count + schoolStats.count,
          students: studentStats.count,
          cost: (
            calcCost(publicStats) +
            calcCost(schoolStats) +
            calcCost(studentStats)
          ).toFixed(2),
        },
        schoolsByCost: schoolUsage
      });

    } catch (err) {
      console.error("❌ Error fetching usage stats:", err);
    } finally {
      setLoadingUsage(false);
    }
  };

  useEffect(() => {
    fetchUsageStats();
  }, []);

  /* ---------------------- Non-usage data fetches ---------------------- */

  const fetchData = async () => {
  const dbi = getFirestore();

  // USERS (for owner lookup + Admin tab)
  const userSnap = await getDocs(collection(dbi, 'users'));
  const allUsers = userSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  setUsers(allUsers);

  // SCHOOLS (may be minimal: { ownerId, createdAt })
  const schoolSnap = await getDocs(collection(dbi, 'schools'));
  const schoolsDocs = schoolSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // TEACHERS (both teachers/ collection and users/ with role:'teacher')
  const teacherColSnap = await getDocs(collection(dbi, 'teachers'));
  const teachersCol = teacherColSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  const userTeachers = allUsers.filter(u => (u.role || '').toLowerCase() === 'teacher');
  

  // STUDENTS
  const studentSnap = await getDocs(collection(dbi, 'students'));
  const allStudents = studentSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // For labelling
  const { schoolMap, userById } = buildSchoolMap(schoolsDocs, allUsers);

 // Merge teachers from both sources (dedupe by uid/id)
const teacherByUid = new Map();
[...teachersCol, ...userTeachers].forEach(t => {
  const uid = t.uid || t.id; // teachers/{uid} doc id matches users/{uid}
  if (!uid) return;
  // Prefer teachers/ doc if it exists (usually has className, tempPassword etc.)
  const existing = teacherByUid.get(uid);
  if (!existing || existing.__source !== "teachers") {
    teacherByUid.set(uid, { ...t, __source: t.className ? "teachers" : "users" });
  }
});

const dedupedTeachers = Array.from(teacherByUid.values());

// Count maps by schoolId/orgId (deduped)
const teacherCountMap = {};
dedupedTeachers.forEach(t => {
  const sid = t.schoolId || t.orgId || null;
  if (sid) teacherCountMap[sid] = (teacherCountMap[sid] || 0) + 1;
});


  const studentCountMap = {};
  allStudents.forEach(s => {
    const sid = s.schoolId || s.orgId || null;
    if (sid) studentCountMap[sid] = (studentCountMap[sid] || 0) + 1;
  });

  // Real schools with preserved/derived labels
const baseSchools = schoolsDocs.map(s => {
  const created =
    (s.createdAt?.toDate ? s.createdAt.toDate() :
     s.createdAt ? new Date(s.createdAt) : null);

  // NEW: support both ownerId and ownerUid
  const ownerId = s.ownerId || s.ownerUid || null;

  const ownerLabel = ownerId
    ? pick(
        userById[ownerId]?.fullName,
        userById[ownerId]?.name,
        userById[ownerId]?.email,
        ownerId
      )
    : "—";

  // NEW: compute renewal date for schools from Stripe subscription, fallback to createdAt
  const rawRenewal =
    s.subscription?.currentPeriodEnd ||
    s.plan?.currentPeriodEnd ||
    null;

  let renewalDisplay = "—";
  if (rawRenewal) {
    renewalDisplay = formatUkDate(rawRenewal);
  } else if (created) {
    // fallback: show createdAt if no subscription end yet
    renewalDisplay = formatUkDate(created);
  }

  return {
    id: s.id,
    name: schoolMap[s.id],            // ✅ old or new label
    ownerId,
    ownerLabel,
    createdAt: created,
    renewalDate: isInvoiceSchool(s)
  ? `${renewalDisplay} (Invoice)`
  : renewalDisplay,
      // ✅ add this
    teacherCount: teacherCountMap[s.id] || 0,
    studentCount: studentCountMap[s.id] || 0,
  };
});


  // Synthetic schools: referenced by teacher/student but no schools/{id} doc
  const known = new Set(schoolsDocs.map(s => s.id));
  const referenced = new Set([
    ...Object.keys(teacherCountMap),
    ...Object.keys(studentCountMap),
  ]);

  const synthetic = Array.from(referenced)
    .filter(id => id && !known.has(id))
    .map(id => ({
      id,
      name: `School ${String(id).slice(0, 6)}`,
      ownerId: null,
      ownerLabel: '—',
      createdAt: null,
      teacherCount: teacherCountMap[id] || 0,
      studentCount: studentCountMap[id] || 0,
    }));

  setSchools([...baseSchools, ...synthetic].sort((a, b) => a.name.localeCompare(b.name)));

  // ADMINS
  const adminSnap = await getDocs(collection(dbi, 'admins'));
  setAdmins(adminSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

  // CONTACT MESSAGES
  const msgSnap = await getDocs(query(collection(dbi, 'contactMessages'), orderBy('timestamp', 'desc')));
  setMessages(msgSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

// INVOICE REQUESTS
const invSnap = await getDocs(
  query(collection(dbi, "invoiceRequests"), orderBy("createdAt", "desc"))
);

setInvoiceRequests(invSnap.docs.map(d => ({ id: d.id, ...d.data() })));
};

const sendInvoice = async (requestId) => {
  const user = auth.currentUser;
  const token = await user.getIdToken(true);

  // IMPORTANT: fetch request doc so we can enforce deal is set
  const reqDoc = invoiceRequests.find(r => r.id === requestId);
  const deal = reqDoc?.deal || reqDoc?.dealOverride;

  if (!deal?.seats || !deal?.amount) {
    throw new Error("Deal not set yet. Click 'Set Deal' first (seats + amount).");
  }

  const url = "https://us-central1-yourstoryworld-df05d.cloudfunctions.net/sendInvoiceForRequestHttp";

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      requestId,
      deal, // ✅ send confirmed values to function
    }),
  });

  const data = await resp.json();
  if (!resp.ok) throw new Error(data?.error || "Failed to send invoice");
  return data;
};


const resendInvoice = async (requestId) => {
  const user = auth.currentUser;
  const token = await user.getIdToken(true);

  const url =
    "https://us-central1-yourstoryworld-df05d.cloudfunctions.net/resendInvoiceForRequestHttp";

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ requestId }),
  });

  const data = await resp.json();
  if (!resp.ok) throw new Error(data?.error || "Failed to resend invoice");
  return data;
};

const activateSchool = async (requestId) => {
  const user = auth.currentUser;
  const token = await user.getIdToken(true);

  const url =
    "https://us-central1-yourstoryworld-df05d.cloudfunctions.net/activateSchoolFromInvoiceRequestHttp";

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ requestId }),
  });

  const data = await resp.json();
  if (!resp.ok) throw new Error(data?.error || "Failed to activate school");
  return data;
};

const adminCreateSchoolSilent = async (payload) => {
  const user = auth.currentUser;
  const token = await user.getIdToken(true);

  const url =
    "https://us-central1-yourstoryworld-df05d.cloudfunctions.net/adminCreateSchoolSilentHttp";

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await resp.json();
  if (!resp.ok) throw new Error(data?.error || "Failed to create school");
  return data;
};


const openDealModalForRow = (row) => {
  const packageKey = row.packageKey || row.planKey || row.packageRef?.packageKey || "school_100";

  const defaults = getPlanDefaults(packageKey);

  // If you've already saved a deal before, prefer it
  const savedDeal = row.deal || row.dealOverride || null;

  setDealTarget(row);

  setDealForm({
    packageKey,
    displayName: savedDeal?.displayName || defaults?.displayName || row.schoolPlanName || "School Plan",
    billingInterval: savedDeal?.billingInterval || defaults?.billingInterval || "annual",
    currency: savedDeal?.currency || defaults?.currency || "GBP",

    // 250+ defaults are blank by design
    seats: savedDeal?.seats ?? defaults?.seats ?? "",
    amount: savedDeal?.amount ?? defaults?.amount ?? "",

    notes: savedDeal?.notes || "",
  });

  setDealModalOpen(true);
};

const saveDealToRequest = async () => {
  if (!dealTarget?.id) return;

  // Basic validation: you can’t create invoice without seats+amount
  if (!dealForm.seats || !dealForm.amount) {
    alert("Please enter both Seats and Amount before saving.");
    return;
  }

  setDealLoading(true);
  try {
    await updateDoc(doc(db, "invoiceRequests", dealTarget.id), {
      packageKey: dealForm.packageKey,
      deal: {
        packageKey: dealForm.packageKey,
        displayName: dealForm.displayName,
        billingInterval: dealForm.billingInterval,
        currency: dealForm.currency,
        seats: Number(dealForm.seats),
        amount: Number(dealForm.amount),
        notes: dealForm.notes || "",
        updatedAt: serverTimestamp(),
      },
      // optional but helpful for your flow:
      updatedAt: serverTimestamp(),
    });

    alert("✅ Deal saved to invoice request.");
    setDealModalOpen(false);
    setDealTarget(null);
    fetchData();
  } catch (e) {
    console.error("saveDealToRequest failed:", e);
    alert("Failed to save deal: " + (e?.message || e));
  } finally {
    setDealLoading(false);
  }
};




  useEffect(() => {
    fetchData();
  }, []);

  const handleTabChange = (e, val) => setActiveTab(val);

  /* ---------------------- User Management grid (top tab) ---------------------- */

  const planFilters = ['all', 'free', 'personal', 'family'];
  const currentPlan = planFilters[planTab];

 const baseUserRows = users
  .filter(u => {
    const role = (u.role || '').toLowerCase();
    return role !== 'admin' && role !== 'student';
  })
  .map(u => {
    const bucket = deriveBucket(u);

    let renewalDateDisplay = '';

    // --- 1) Prefer manual renewalDate if present ---
    if (u.renewalDate) {
      const d = new Date(u.renewalDate);
      if (!isNaN(d.getTime())) {
        renewalDateDisplay = d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }
    }

    // --- 2) Fall back to Stripe / subscription.currentPeriodEnd ---
    if (!renewalDateDisplay && u.subscription?.currentPeriodEnd) {
      const raw = u.subscription.currentPeriodEnd;

      let d = null;

      if (raw?.toDate) {
        d = raw.toDate();                         // Firestore Timestamp
      } else if (typeof raw === "number") {
        d = new Date(raw > 1e12 ? raw : raw * 1000); // ms vs seconds
      } else if (typeof raw === "string") {
        d = new Date(raw);                        // ISO
      }

      if (d && !isNaN(d.getTime())) {
        renewalDateDisplay = d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }

      }
          const activity = Array.isArray(u.readingActivity) ? u.readingActivity : [];

    const lastActive =
      (u.lastActive?.toDate ? u.lastActive.toDate() : (u.lastActive ? new Date(u.lastActive) : null)) ||
      (u.lastRead?.toDate ? u.lastRead.toDate() : (u.lastRead ? new Date(u.lastRead) : null)) ||
      latestReadingActivityDate(activity) ||
      (u.planUpdatedAt?.toDate ? u.planUpdatedAt.toDate() : (u.planUpdatedAt ? new Date(u.planUpdatedAt) : null)) ||
      (u.createdAt?.toDate ? u.createdAt.toDate() : (u.createdAt ? new Date(u.createdAt) : null));

    

    // Convert subscription.currentPeriodEnd into a sortable Date (or null)
const rawCpe = u?.subscription?.currentPeriodEnd ?? u?.stripe?.currentPeriodEnd ?? null;
const currentPeriodEndDate =
  rawCpe?.toDate ? rawCpe.toDate() :
  (typeof rawCpe === "number" ? new Date(rawCpe > 1e12 ? rawCpe : rawCpe * 1000) :
  (typeof rawCpe === "string" ? new Date(rawCpe) : null));

return {
  id: u.id,
  email: u.email || "—",
  fullName: u.fullName || u.name || "—",

  plan: planDisplayOf(u),
  bucket,
  role: u.role || "user",

  // what you already had
  renewalDate: renewalDateDisplay,
  lastActive,

  // ✅ these now match your DataGrid column "field" names
  provider: providerOf(u),
  subscriptionStatus: subStatusOf(u),
  cancelAtRenewal: cancelFlagOf(u),
  currentPeriodEnd: (currentPeriodEndDate && !isNaN(currentPeriodEndDate.getTime()))
    ? currentPeriodEndDate
    : null,

  emailVerificationOverride: !!u.emailVerificationOverride,
};

  });
;



  const filteredUsers = baseUserRows.filter(u => {
    if (!includeSchools && u.bucket === 'school') return false;
    if (currentPlan === 'all') return true;
    return u.bucket === currentPlan;
  });

  const userColumns = [
    { field: "email", headerName: "Email", flex: 3, minWidth: 210 },
    { field: "fullName", headerName: "Name", flex: 2, minWidth: 200 },

    { field: "plan", headerName: "Plan", width: 170 },
    { field: "bucket", headerName: "Bucket", width: 120 },
    { field: "role", headerName: "Role", width: 110 },

    // ✅ the columns you were angry got removed:
  { field: "provider", headerName: "Provider", width: 110 },
{ field: "subscriptionStatus", headerName: "Status", width: 130 },
{
  field: "cancelAtRenewal",
  headerName: "Cancel @ renewal",
  width: 150,
  valueFormatter: ({ value }) => (value ? "Yes" : "No"),
},
{
  field: "currentPeriodEnd",
  headerName: "Period End",
  width: 130,
  valueFormatter: ({ value }) =>
    value instanceof Date ? value.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—",
},
{
  field: "lastActive",
  headerName: "Last Active",
  width: 190,
  valueFormatter: ({ value }) =>
    value instanceof Date ? value.toLocaleString("en-GB") : "—",
},


    { field: "renewalDate", headerName: "Renewal Date", width: 140 },

    {
      field: "emailVerificationOverride",
      headerName: "Verify Override",
      width: 140,
      valueFormatter: ({ value }) => (value ? "Yes" : "No"),
    },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 320,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <>
        <Button
          size="small"
          sx={{ mr: 1 }}
          onClick={() => {
            const bucket = params.row.bucket;
            const uid = params.row.id;

            if (bucket === 'family') {
              navigate(`/admin/view-family/${uid}`, { state: { adminView: true } });
            } else if (bucket === 'personal' || bucket === 'free' || bucket === 'other') {
              navigate(`/admin/view-personal/${uid}`, { state: { adminView: true } });
            } else if (bucket === 'school') {
              navigate(`/admin/view-school/${uid}`, { state: { adminView: true } });
            } else {
              alert('No dashboard view configured.');
            }
          }}
        >
          View
        </Button>

        <Button
          size="small"
          onClick={() => {
            setEditTarget('user');
            setEditData(params.row);
            setEditModalOpen(true);
          }}
        >
          ✏️
        </Button>

        <Button
          size="small"
          color="error"
          onClick={async () => {
            if (!window.confirm("Delete this user completely?")) return;
            await adminDeleteAccount(params.row.id, "user");
            fetchData();
            fetchUsageStats();
          }}
        >
          🗑️
        </Button>
      </>
    )
  }
];



  /* ---------------------- UI ---------------------- */

  const resetNewUserData = () => {
    setNewUserData({
      email: "",
      fullName: "",
      country: "",
      plan: "Free Plan",
      renewalDate: "",
      password: "",
    });
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 500,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    maxHeight: "90vh",
    overflowY: "auto",
  };

  const resetNewSchoolData = () => {
  setNewSchoolData({
    schoolName: '',
    email: '',
    contactName: '',
    country: '',
    phoneNumber: '',
    renewalDate: '',

    packageKey: 'school_100',
    billingInterval: 'annual',
    seats: 100,

    password: ''
  });
};


 const openSchoolModal = async (school) => {
  setSelectedSchool(school);
  setSchoolModalOpen(true);

  const dbi = getFirestore();

  // TEACHERS: from teachers/ (schoolId OR orgId)
  const [tBySchool, tByOrg] = await Promise.all([
    getDocs(query(collection(dbi, 'teachers'), where('schoolId', '==', school.id))),
    getDocs(query(collection(dbi, 'teachers'), where('orgId', '==', school.id))),
  ]);
  const teachersFromCol = [...tBySchool.docs, ...tByOrg.docs]
    .map(d => ({ id: d.id, ...d.data() }));

  // TEACHERS: also from users/ (role === 'teacher')
  const usersSnap = await getDocs(collection(dbi, 'users'));
  const teacherUsers = usersSnap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(u => (u.role || '').toLowerCase() === 'teacher' && (u.schoolId === school.id || u.orgId === school.id));

  // Merge teachers from both sources (by id+email to dedupe)
  const teacherKey = (t) => (t.id || '') + '|' + (t.email || '');
  const teacherMap = new Map();
  [...teachersFromCol, ...teacherUsers].forEach(t => teacherMap.set(teacherKey(t), t));
  setSelectedTeachers(Array.from(teacherMap.values()));

  // STUDENTS: schoolId OR orgId
  const [sBySchool, sByOrg] = await Promise.all([
    getDocs(query(collection(dbi, 'students'), where('schoolId', '==', school.id))),
    getDocs(query(collection(dbi, 'students'), where('orgId', '==', school.id))),
  ]);
  const students = [...sBySchool.docs, ...sByOrg.docs].map(d => ({ id: d.id, ...d.data() }));

  // Deduplicate students (by id+username)
  const studentKey = (s) => (s.id || '') + '|' + (s.username || '');
  const studentMap = new Map();
  students.forEach(s => studentMap.set(studentKey(s), s));
  setSelectedStudents(Array.from(studentMap.values()));
};


  const adminColumns = [
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'role', headerName: 'Role', width: 120 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Button
          color="error"
          onClick={async () => {
            if (window.confirm("Remove this admin?")) {
              await deleteDoc(doc(db, 'admins', params.row.id));
              fetchData();
            }
          }}
        >
          Remove
        </Button>
      )
    }
  ];

  const handleDeleteSchool = async (schoolId) => {
    if (!window.confirm('Delete this school and all linked accounts? (Auth + Firestore)')) return;
    try {
      await adminDeleteAccount(schoolId, "school");
      alert('School deleted.');
      fetchData();
      fetchUsageStats();
    } catch (error) {
      console.error('adminDeleteAccount(school) failed:', error);
      alert('Failed to delete school: ' + (error?.message || error));
     }
  };

  // Debug rows (first 50)
  const debugRows = users.slice(0, 50).map(u => ({
    id: u.id,
    email: u.email || '—',
    role: u.role || '—',
    bucket: deriveBucket(u),
    hasSub: !!(u.subscription || u.stripe),
    planField: u.plan || '—',
    subDisplay: u?.subscription?.displayName || u?.stripe?.displayName || '—',
    kind: readKind(u) || '—',
    key: readKey(u) || '—'
  }));

const openEditSchoolModal = (school) => {
  setSchoolEditData(school);
  setSchoolEditModalOpen(true);
};

const handleSaveSchoolEdits = async () => {
  if (!schoolEditData?.id) return;
  const dbi = getFirestore();
  const { id, name } = schoolEditData;

  try {
    // Update display name on schools/{id}
    await updateDoc(doc(dbi, "schools", id), {
      name: name || null,
    }).catch(() => {});

    // Also update matching users/{id} doc if it exists
    await updateDoc(doc(dbi, "users", id), {
      name: name || null,
      schoolName: name || null,
    }).catch(() => {});

    alert("School updated.");
    setSchoolEditModalOpen(false);
    setSchoolEditData(null);
    fetchData();
    fetchUsageStats();
  } catch (e) {
    console.error("Update school failed:", e);
    alert("Failed to update school: " + (e?.message || e));
  }
};

const schoolColumns = [
  { field: "name", headerName: "School", flex: 1, minWidth: 220 },
  { field: "id", headerName: "School ID", flex: 1, minWidth: 220 },
  { field: "ownerLabel", headerName: "Owner", flex: 1, minWidth: 220 },
  {
    field: "renewalDate",
    headerName: "Renews",
    width: 140,
  },
  {
    field: "createdAt",
    headerName: "Created",
    width: 180,
    valueFormatter: ({ value }) =>
      value instanceof Date ? value.toLocaleString("en-GB") : "—",
  },
  { field: "teacherCount", headerName: "# Teachers", width: 120 },
  { field: "studentCount", headerName: "# Students", width: 120 },
  {
    field: "actions",
    headerName: "Actions",
    width: 260,
    renderCell: (params) => (
      <>
        <Button
          variant="outlined"
          size="small"
          sx={{ mr: 1 }}
          onClick={() => openSchoolModal(params.row)}
        >
          View
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={{ mr: 1 }}
          onClick={() => openEditSchoolModal(params.row)}
        >
          Edit
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => handleDeleteSchool(params.row.id)}
        >
          Delete
        </Button>
      </>
    ),
  },
];





  return (
    <Box sx={{ fontFamily: 'Montserrat', minHeight: '100vh', backgroundColor: '#f7f9fc' }}>
      <AppBar position="static">
        <Toolbar>
  <Typography variant="h6" sx={{ flexGrow: 1 }}>
    Admin Dashboard
  </Typography>

  <Button
    color="inherit"
    onClick={() =>
      signOut(getAuth()).then(() => (window.location.href = "/admin-login"))
    }
  >
    Logout
  </Button>
</Toolbar>

      </AppBar>

      <Box padding={3}>
        <Tabs value={activeTab} onChange={handleTabChange} centered>
          <Tab label="User Management" />
          <Tab label="School Registration" />
          <Tab label="Admin Roles" />
          <Tab label="📬 Contact Messages" />
          <Tab label="🧾 Invoice Requests" />
          <Tab label="📊 Usage Stats" />
        </Tabs>

        {/* User Management */}
        {activeTab === 0 && (
          <Box mt={3}>
            <Typography variant="h6" mb={0.5}>Users</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Loaded: {users.length} • Showing: {filteredUsers.length}
            </Typography>

                <Box display="flex" justifyContent="flex-end" mb={2}>
      <Button
  variant="contained"
  onClick={() => {
    resetNewUserData();
    setNewUserModalOpen(true);
  }}
>
  + Add User
</Button>

    </Box>


            <Box mb={1} display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Tabs value={planTab} onChange={(e, v) => setPlanTab(v)}>
                <Tab label="All" />
                <Tab label="Free" />
                <Tab label="Personal" />
                <Tab label="Family" />
              </Tabs>

              <FormControlLabel
                control={
                  <Switch
                    checked={includeSchools}
                    onChange={(e) => setIncludeSchools(e.target.checked)}
                  />
                }
                label="Include school accounts"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={showDebug}
                    onChange={(e) => setShowDebug(e.target.checked)}
                  />
                }
                label="Show debug"
              />
            </Box>

            <DataGrid
              rows={filteredUsers}
              columns={userColumns}
              autoHeight
              pageSizeOptions={[5, 10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            />

            <Collapse in={showDebug} unmountOnExit>
              <Paper sx={{ mt: 2, p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Raw users (first 50)</Typography>
                <div style={{ height: 400, width: '100%' }}>
                  <DataGrid
                    rows={debugRows}
                    columns={[
                      { field: 'id', headerName: 'UID', width: 250 },
                      { field: 'email', headerName: 'Email', flex: 1 },
                      { field: 'role', headerName: 'Role', width: 120 },
                      { field: 'bucket', headerName: 'Bucket', width: 120 },
                      { field: 'hasSub', headerName: 'Has Sub', width: 100 },
                      { field: 'planField', headerName: 'plan field', width: 150 },
                      { field: 'subDisplay', headerName: 'sub display', width: 180 },
                      { field: 'kind', headerName: 'kind', width: 130 },
                      { field: 'key', headerName: 'key', width: 180 },
                    ]}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                  />
                </div>
              </Paper>
            </Collapse>
          </Box>
        )}

        {/* School Registration */}
        {activeTab === 1 && (
  <Box mt={3}>
    <Typography variant="h6" mb={2}>Registered Schools</Typography>
    <Box display="flex" justifyContent="flex-end" mb={2}>
      <Button
        variant="contained"
        onClick={() => {
  resetNewSchoolData();
  setNewSchoolModalOpen(true);
}}

      >
        + Add School
      </Button>
    </Box>

    <DataGrid
      rows={allSchools}
      columns={schoolColumns}
      autoHeight
      pageSizeOptions={[5, 10, 25]}
      initialState={{ pagination: { paginationModel: { pageSize: 5, page: 0 } } }}
    />
  </Box>
        )}

        {/* Admin Roles */}
        {activeTab === 2 && (
          <Box mt={3}>
            <Typography variant="h6" mb={2}>Admin Roles</Typography>
            <DataGrid rows={admins} columns={adminColumns}
 autoHeight pageSizeOptions={[5, 10, 25]} />
            <Box mt={3} display="flex" gap={2}>
              <TextField label="Email" value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} />
              <Select value={newAdminRole} onChange={(e) => setNewAdminRole(e.target.value)}>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="moderator">Moderator</MenuItem>
              </Select>
              <Button variant="contained" onClick={async () => {
                if (!newAdminEmail) return;
                await setDoc(doc(db, 'admins', newAdminEmail), {
                  email: newAdminEmail,
                  role: newAdminRole,
                });
                setNewAdminEmail('');
                fetchData();
              }}>
                Add Admin
              </Button>
            </Box>
          </Box>
        )}

        {/* Contact Messages */}
{activeTab === 3 && (() => {
  // Normalise type: treat missing type as "contact"
  const taggedMessages = messages.map((msg) => ({
    ...msg,
    _type:
      msg.type ||
      (msg.subject && msg.subject.toLowerCase().includes("feedback")
        ? "feedback"
        : "contact"),
  }));

  const filteredMessages =
    messageFilter === "all"
      ? taggedMessages
      : taggedMessages.filter((m) => m._type === messageFilter);

  return (
    <Box mt={3}>
      <Typography variant="h6" mb={1}>
        Contact & Feedback Messages
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        Total: {messages.length} · Showing: {filteredMessages.length}
      </Typography>

      {/* Filter row */}
      <Box display="flex" gap={2} alignItems="center" mb={2} flexWrap="wrap">
        <Typography variant="body2">Filter by type:</Typography>
        <Select
          size="small"
          value={messageFilter}
          onChange={(e) => setMessageFilter(e.target.value)}
          sx={{ minWidth: 180, backgroundColor: "#fff" }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="contact">Contact messages</MenuItem>
          <MenuItem value="feedback">Feedback / issues</MenuItem>
        </Select>
      </Box>

      {filteredMessages.length === 0 ? (
        <Typography>No messages found for this filter.</Typography>
      ) : (
        filteredMessages.map((msg) => {
          const isFeedback = msg._type === "feedback";
          const labelColour = isFeedback ? "#1976D2" : "#388E3C";
          const labelBg = isFeedback ? "rgba(25, 118, 210, 0.08)" : "rgba(56, 142, 60, 0.08)";

          return (
            <Box
              key={msg.id}
              p={2}
              mb={2}
              bgcolor="#fff"
              borderRadius="8px"
              border="1px solid #e0e0e0"
            >
              {/* Top row: type + subject */}
              <Box display="flex" justifyContent="space-between" flexWrap="wrap" mb={0.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      padding: "2px 8px",
                      borderRadius: 999,
                      backgroundColor: labelBg,
                      color: labelColour,
                    }}
                  >
                    {isFeedback ? "Feedback" : "Contact"}
                  </span>
                  {msg.source && (
                    <Typography variant="caption" color="text.secondary">
                      from: {msg.source}
                    </Typography>
                  )}
                </Box>

                <Typography variant="caption" color="text.secondary">
                  {msg.timestamp?.toDate
                    ? new Date(msg.timestamp.toDate()).toLocaleString("en-GB")
                    : "No date"}
                </Typography>
              </Box>

              {/* Subject / name */}
              <Typography variant="subtitle2" mt={0.5}>
                {msg.subject || (isFeedback ? "Feedback message" : "Contact message")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {msg.name || "Anonymous"} {msg.email && `(${msg.email})`}
              </Typography>

              {/* Message body */}
              <Typography mt={1}>{msg.message}</Typography>

              {/* Actions */}
              <Box mt={1.5} display="flex" gap={2} flexWrap="wrap">
                {!msg.resolved && (
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    onClick={async () => {
                      await updateDoc(doc(db, "contactMessages", msg.id), { resolved: true });
                      fetchData();
                    }}
                  >
                    Mark as resolved
                  </Button>
                )}
                {msg.resolved && (
                  <Typography
                    variant="caption"
                    sx={{ alignSelf: "center", color: "success.main", fontWeight: 600 }}
                  >
                    ✅ Resolved
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={async () => {
                    if (window.confirm("Delete this message?")) {
                      await deleteDoc(doc(db, "contactMessages", msg.id));
                      fetchData();
                    }
                  }}
                >
                  Delete
                </Button>
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
})()}


        {/* Invoice Requests */}
{activeTab === 4 && (() => {
  const statusOf = (r) => (r.status || "pending").toLowerCase();

  const filtered =
    invoiceFilter === "all"
      ? invoiceRequests
      : invoiceRequests.filter(r => statusOf(r) === invoiceFilter);

  const rows = filtered.map(r => ({
    ...r,
    _createdAt: r.createdAt?.toDate ? r.createdAt.toDate() : null,
  }));

  const statusOptions = ["pending", "contacted", "invoiced", "paid", "activated"];

  const columns = [
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        const s = statusOf(params.row);
        const label = s.charAt(0).toUpperCase() + s.slice(1);
        return (
          <Box
            sx={{
              fontSize: "0.8rem",
              fontWeight: 700,
              px: 1.2,
              py: 0.3,
              borderRadius: 999,
              border: "1px solid rgba(0,0,0,0.12)",
              bgcolor: "rgba(25,118,210,0.06)",
            }}
          >
            {label}
          </Box>
        );
      }
    },
    {
  field: "billingType",
  headerName: "Billing",
  width: 120,
  renderCell: (params) => {
  // Invoice Requests are ONLY for offline / non-Stripe invoicing in our system
const isInvoice = true;


    return (
      <Box
        sx={{
          fontSize: "0.75rem",
          fontWeight: 700,
          px: 1.2,
          py: 0.3,
          borderRadius: 999,
          bgcolor: isInvoice ? "rgba(245, 124, 0, 0.12)" : "rgba(46, 125, 50, 0.12)",
          color: isInvoice ? "#EF6C00" : "#2E7D32",
          border: "1px solid rgba(0,0,0,0.12)",
        }}
      >
        {isInvoice ? "Invoice" : "Stripe"}
      </Box>
    );
  },
},

    { field: "schoolName", headerName: "School", flex: 1, minWidth: 200 },
    { field: "contactName", headerName: "Contact", flex: 1, minWidth: 160 },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <a href={`mailto:${params.value}`} style={{ textDecoration: "none" }}>
          {params.value}
        </a>
      ),
    },
    { field: "country", headerName: "Country", width: 130 },
    { field: "phoneNumber", headerName: "Phone", width: 150 },
    {
      field: "_createdAt",
      headerName: "Requested",
      width: 170,
      valueFormatter: ({ value }) =>
        value instanceof Date ? value.toLocaleString("en-GB") : "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 760,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Select
            size="small"
            value={statusOf(params.row)}
            onChange={async (e) => {
              try {
                await updateDoc(doc(db, "invoiceRequests", params.row.id), {
                  status: e.target.value,
                  updatedAt: serverTimestamp(),
                });
                fetchData();
              } catch (err) {
                console.error("update invoice status failed:", err);
                alert("Failed to update status.");
              }
            }}
            sx={{ minWidth: 130, bgcolor: "#fff" }}
          >
            {statusOptions.map(s => (
              <MenuItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </Select>

          <Button
  size="small"
  variant="contained"
  onClick={() => openDealModalForRow(params.row)}
>
  Set Deal
</Button>


          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const payload = JSON.stringify(params.row, null, 2);
              navigator.clipboard.writeText(payload);
              alert("Request copied to clipboard.");
            }}
          >
            Copy
          </Button>

          {(params.row.pdfDownloadUrl || params.row.pdfUrl) && (
  <Button
    size="small"
    variant="outlined"
    onClick={() => {
      const url = params.row.pdfDownloadUrl || params.row.pdfUrl;
      window.open(url, "_blank", "noopener,noreferrer");
    }}
  >
    View PDF
  </Button>
)}



          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={async () => {
              if (!window.confirm("Delete this invoice request?")) return;
              try {
                await deleteDoc(doc(db, "invoiceRequests", params.row.id));
                fetchData();
              } catch (err) {
                console.error("delete invoice request failed:", err);
                alert("Failed to delete request.");
              }
            }}
          >
            Delete
          </Button>

          <Button
  size="small"
  variant="contained"
  onClick={async () => {
    try {
      await sendInvoice(params.row.id);
      alert("Invoice sent ✅");
      fetchData(); // refresh table
    } catch (e) {
      alert(e.message);
    }
  }}
>
  Send Invoice
</Button>

<Button
  size="small"
  variant="outlined"
  onClick={async () => {
    try {
      await resendInvoice(params.row.id);
      alert("Invoice resent ✅");
      fetchData();
    } catch (e) {
      alert(e.message);
    }
  }}
>
  Resend
</Button>

<Button
  size="small"
  variant="contained"
  color="success"
  onClick={async () => {
    try {
      if (!window.confirm("Activate this school and email them a password reset link?")) return;
      const out = await activateSchool(params.row.id);
      alert(`Activated ✅\n\nUID: ${out.uid}\nEmail: ${out.email}`);
      fetchData();
      fetchUsageStats();
    } catch (e) {
      alert(e.message);
    }
  }}
>
  Activate
</Button>


        </Box>
      )
    }
  ];

  return (
    <Box mt={3}>
      <Typography variant="h6" mb={0.5}>Invoice Requests</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        Total: {invoiceRequests.length} · Showing: {filtered.length}
      </Typography>

      <Box display="flex" gap={2} alignItems="center" mb={2} flexWrap="wrap">
        <Typography variant="body2">Filter by status:</Typography>
        <Select
          size="small"
          value={invoiceFilter}
          onChange={(e) => setInvoiceFilter(e.target.value)}
          sx={{ minWidth: 220, bgcolor: "#fff" }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="contacted">Contacted</MenuItem>
          <MenuItem value="invoiced">Invoiced</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="activated">Activated</MenuItem>
        </Select>
     </Box>
     <Box sx={{ width: "100%", overflowX: "auto" }}>
  <Box sx={{ minWidth: 2000 }}>
    <DataGrid
      rows={Array.isArray(rows) ? rows : []}
      columns={Array.isArray(columns) ? columns : []}
      getRowId={(r) => r.id}
      autoHeight
      rowHeight={64}
      pageSizeOptions={[5, 10, 25, 50]}
      initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
    />
  </Box>
</Box>
 </Box>

  );
})()}



        {/* Usage Stats */}
        {activeTab === 5 && (
          <div style={{ padding: '1rem' }}>
            <Typography variant="h6" gutterBottom>
              📊 Usage Stats (Lifetime)
            </Typography>

            {usageSummary && (
              <Box mb={3} p={2} bgcolor="#fff" borderRadius={2} boxShadow={1}>
                <Typography variant="subtitle1" mb={1}>Individual Users (Free/Personal/Family)</Typography>
                <Typography>Users: {usageSummary.public?.count ?? '—'}</Typography>
                <Typography>Stories (Lifetime): {usageSummary.public?.stories ?? 0}</Typography>
                <Typography>TTS (Lifetime): {usageSummary.public?.tts ?? 0}</Typography>
                <Typography>💷 Cost (est): £{usageSummary.public?.cost ?? '—'}</Typography>

                <Box mt={2}>
                  <Typography variant="subtitle1">School Accounts (Teachers)</Typography>
                  <Typography>Accounts: {usageSummary.schools?.count ?? '—'}</Typography>
                  <Typography>Stories (Lifetime): {usageSummary.schools?.stories ?? 0}</Typography>
                  <Typography>TTS (Lifetime): {usageSummary.schools?.tts ?? 0}</Typography>
                  <Typography>💷 Cost (est): £{usageSummary.schools?.cost ?? '—'}</Typography>
                </Box>

                <Box mt={2}>
                  <Typography variant="subtitle1">Students</Typography>
                  <Typography>Students: {usageSummary.students?.count ?? '—'}</Typography>
                  <Typography>Stories (Lifetime): {usageSummary.students?.stories ?? 0}</Typography>
                  <Typography>TTS (Lifetime): {usageSummary.students?.tts ?? 0}</Typography>
                  <Typography>💷 Cost (est): £{usageSummary.students?.cost ?? '—'}</Typography>
                </Box>

                <Box mt={2}>
                  <Typography variant="h6">
  💰 Total Estimated Lifetime Cost: £{usageSummary.total?.cost ?? '—'}
</Typography>

                </Box>
              </Box>
            )}

            {/* Per-School breakdown */}
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Schools (aggregate from students)</Typography>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <div style={{ minWidth: 900 }}>
                <DataGrid
                  rows={(usageSummary?.schoolsByCost || []).map((r, i) => ({ id: i, ...r }))}
                  loading={loadingUsage}
                  columns={[
                    { field: "name", headerName: "School", flex: 1, minWidth: 220 },
                    { field: "studentCount", headerName: "# Students", width: 130 },
                    { field: "stories", headerName: "Stories (Lifetime)", width: 170 },
                    { field: "tts", headerName: "TTS (Lifetime)", width: 150 },
                    { field: "cost", headerName: "Est. Cost (£)", width: 140 },
                  ]}
                  autoHeight
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                />
              </div>
            </div>

            {/* Users table (always visible) */}
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Users (Free / Personal / Family)</Typography>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <div style={{ minWidth: 1000 }}>
                <DataGrid
                  rows={usageUsers.filter(u => ['free','personal','family','other'].includes(u.bucket))}
                  loading={loadingUsage}
                  columns={[
                    { field: "fullName", headerName: "Name", flex: 1, minWidth: 220 },
                    { field: "email", headerName: "Email", flex: 1, minWidth: 240 },
                    { field: "plan", headerName: "Plan", width: 200 },
                    { field: "storiesLifetime", headerName: "Stories (Lifetime)", width: 170 },
                    { field: "ttsPlaybacks", headerName: "TTS (Lifetime)", width: 150 },
                    { field: "totalReadingTime", headerName: "Time (min)", width: 130 },
                    {
                      field: "lastActive",
                      headerName: "Last Active",
                      width: 180,
                      valueFormatter: ({ value }) =>
                        value instanceof Date ? value.toLocaleString("en-GB") : "—",
                    },
                  ]}
                  autoHeight
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                />
              </div>
            </div>

            {/* Students table (below Schools, horizontally scrollable) */}
            <Box mt={3} mb={1} display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle1">Students</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">Filter by school:</Typography>
                <Select
                  size="small"
                  value={schoolFilter}
                  onChange={(e) => setSchoolFilter(e.target.value)}
                  sx={{ minWidth: 260 }}
                >
                  <MenuItem value="All">All Schools</MenuItem>
                  {schoolOptions.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            <div style={{ width: '100%', overflowX: 'auto' }}>
              <div style={{ minWidth: 1100 }}>
                <DataGrid
                  rows={
                    schoolFilter === "All"
                      ? usageStudents
                      : usageStudents.filter(r => r.schoolId === schoolFilter)
                  }
                  loading={loadingUsage}
                  columns={[
                    { field: "fullName", headerName: "Name", flex: 1, minWidth: 220 },
                    { field: "schoolName", headerName: "School", flex: 1, minWidth: 240 },
                    { field: "email", headerName: "Email", flex: 1, minWidth: 240 },
                    { field: "storiesLifetime", headerName: "Stories (Lifetime)", width: 170 },
                    { field: "ttsPlaybacks", headerName: "TTS (Lifetime)", width: 150 },
                    { field: "totalReadingTime", headerName: "Time (min)", width: 130 },
                    {
                      field: "lastActive",
                      headerName: "Last Active",
                      width: 180,
                      valueFormatter: ({ value }) =>
                        value instanceof Date ? value.toLocaleString("en-GB") : "—",
                    },
                  ]}
                  autoHeight
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                />
              </div>
            </div>
          </div>
        )}

        {/* New User Modal */}
        <Modal
          open={newUserModalOpen}
          onClose={() => {
            resetNewUserData();
            setNewUserModalOpen(false);
          }}
        >
          <Box sx={modalStyle}>
            <Typography variant="h6" mb={2}>Add User</Typography>

            <TextField
              label="Email"
              fullWidth
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Full Name"
              fullWidth
              value={newUserData.fullName}
              onChange={(e) => setNewUserData({ ...newUserData, fullName: e.target.value })}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Country"
              fullWidth
              value={newUserData.country}
              onChange={(e) => setNewUserData({ ...newUserData, country: e.target.value })}
              sx={{ mb: 2 }}
            />

            <Select
              fullWidth
              value={newUserData.plan}
              onChange={(e) => setNewUserData({ ...newUserData, plan: e.target.value })}
              sx={{ mb: 2 }}
            >
              <MenuItem value="Free Plan">Free Plan</MenuItem>
<MenuItem value="Personal Plan">Personal Plan</MenuItem>
<MenuItem value="Family Lite Plan">Family Lite Plan</MenuItem>
<MenuItem value="Big Family Plan">Big Family Plan</MenuItem>


            </Select>

            <TextField
              label="Renewal Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newUserData.renewalDate}
              onChange={(e) => setNewUserData({ ...newUserData, renewalDate: e.target.value })}
              sx={{ mb: 2 }}
            />

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <TextField
                label="Password"
                fullWidth
                value={newUserData.password}
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(newUserData.password);
                  alert("Password copied to clipboard!");
                }}
              >
                Copy
              </Button>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={async () => {
                  const { email, password, fullName, country, renewalDate } = newUserData;
                  if (!email || !password || !fullName || !country) {
                    alert("Please fill in all fields and generate a password.");
                    return;
                  }

                  let provisioningAuth;
                  try {
                    provisioningAuth = getProvisioningAuth();
                    const userCred = await createUserWithEmailAndPassword(provisioningAuth, email, password);
                    const uid = userCred.user.uid;

                    const planKey = toManualPlanKey(newUserData.plan);

// ✅ canonical is what you store in legacy `plan`
const planCanonical = MANUAL_PLAN_CATALOG[planKey]?.label || "Free Plan";

// ✅ displayName is what you show in dashboards/tables
const displayName = manualPlanDisplay(planKey);

// --- compute billing period end from renewalDate (YYYY-MM-DD) ---
const renewalEndMs = renewalDate
  ? new Date(`${renewalDate}T23:59:59.999Z`).getTime()
  : null;

const nowMs = Date.now();
const currentPeriodEndMs =
  Number.isFinite(renewalEndMs) && renewalEndMs > nowMs
    ? renewalEndMs
    : nowMs + 30 * 24 * 60 * 60 * 1000;

// --- map planKey -> monthly quota ---
const monthlyQuota = MANUAL_PLAN_CATALOG[planKey]?.monthlyQuota ?? 5;

await setDoc(doc(db, "users", uid), {
  email,
  emailLower: email.toLowerCase(),
  fullName,
  name: fullName,
  country,
  role: "user",

  // legacy
  plan: planCanonical,
  planKey,

  entitlements: {
    storyQuotaPerMonth: monthlyQuota,
  },

  subscription: {
    source: "manual",
    provider: "manual",
    status: "active",
    billingInterval: "month",
    packageKind: planKey,
    packageKey: planKey,
    displayName, // ✅ "Family Lite Plan" / "Big Family Plan"
    currentPeriodStart: nowMs,
    currentPeriodEnd: currentPeriodEndMs,
  },

  renewalDate: renewalDate || null,

  createdByAdmin: true,
  createdAt: serverTimestamp(),
  planUpdatedAt: serverTimestamp(),
}, { merge: true });



                    alert(`✅ User created!\n\nEmail: ${email}\nPassword: ${password}`);
resetNewUserData();              // ✅ clear fields
setNewUserModalOpen(false);
fetchData();
fetchUsageStats();

                  } catch (err) {
                    console.error("❌ Error creating user:", err);
                    alert("Failed to create user.");
                  } finally {
                    if (provisioningAuth) {
                      try { await signOut(provisioningAuth); } catch {}
                    }
                  }
                }}
              >
                Save User
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={() => {
                  resetNewUserData();
                  setNewUserModalOpen(false);
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Box>
        </Modal>
      </Box>

      {/* Edit User Modal */}
      <Modal open={editModalOpen && editTarget === 'user'} onClose={() => setEditModalOpen(false)}>
        <Box sx={{ backgroundColor: 'white', margin: '10% auto', padding: 4, borderRadius: 2, maxWidth: 500 }}>
          <Typography variant="h6" mb={2}>Edit User</Typography>

          <TextField
            label="Email"
            fullWidth
            value={editData.email || ''}
            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Full Name"
            fullWidth
            value={editData.fullName || ''}
            onChange={(e) => setEditData({ ...editData, fullName: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Select
            fullWidth
            value={editData.plan || ''}
            onChange={(e) => setEditData({ ...editData, plan: e.target.value })}
            sx={{ mb: 2 }}
          >
            <MenuItem value="Free Plan">Free</MenuItem>
<MenuItem value="Personal Plan">Personal</MenuItem>
<MenuItem value="Family Lite Plan">Family Lite</MenuItem>
<MenuItem value="Big Family Plan">Big Family</MenuItem>


          </Select>

          <TextField
            label="Renewal Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={editData.renewalDate || ''}
            onChange={(e) => setEditData({ ...editData, renewalDate: e.target.value })}
            sx={{ mb: 3 }}
          />

                <FormControlLabel
        sx={{ mb: 3 }}
        control={
          <Switch
            checked={!!editData.emailVerificationOverride}
            onChange={(e) =>
              setEditData({
                ...editData,
                emailVerificationOverride: e.target.checked,
              })
            }
          />
        }
        label="Override email verification (allow StoryApp even if not verified)"
      />


          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={async () => {
              const planKey = toManualPlanKey(editData.plan);
const planCanonical = MANUAL_PLAN_CATALOG[planKey]?.label || "Free Plan";


const renewalDate = editData.renewalDate || null;
const renewalEndMs = renewalDate ? new Date(`${renewalDate}T23:59:59.999Z`).getTime() : null;

const nowMs = Date.now();
const currentPeriodEndMs =
  Number.isFinite(renewalEndMs) && renewalEndMs > nowMs
    ? renewalEndMs
    : nowMs + 30 * 24 * 60 * 60 * 1000;

const monthlyQuota = MANUAL_PLAN_CATALOG[planKey]?.monthlyQuota ?? 5;


await updateDoc(doc(db, "users", editData.id), {
  email: editData.email,
  emailLower: (editData.email || "").toLowerCase(),
  fullName: editData.fullName,
  name: editData.fullName,

  plan: planCanonical,
  planKey,
  renewalDate,

  entitlements: { storyQuotaPerMonth: monthlyQuota },

  subscription: {
  source: "manual",
  provider: "manual",
  status: "active",
  billingInterval: "month",
  packageKind: planKey,
  packageKey: planKey,
  displayName: manualPlanDisplay(planKey), // ✅
  currentPeriodStart: nowMs,
  currentPeriodEnd: currentPeriodEndMs,
},


  planUpdatedAt: serverTimestamp(),
  emailVerificationOverride: !!editData.emailVerificationOverride,
});


                setEditModalOpen(false);
                fetchData();
                fetchUsageStats();
              }}
            >
              Save Changes
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={() => setEditModalOpen(false)}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      </Modal>


      <Modal
  open={dealModalOpen}
  onClose={() => setDealModalOpen(false)}
>
  <Box sx={{ ...modalStyle, maxWidth: 600 }}>
    <Typography variant="h6" mb={1}>
      Set Deal / Create Invoice
    </Typography>

    <Typography variant="body2" color="text.secondary" mb={2}>
      {dealTarget?.schoolName || "School"} • {dealTarget?.email || "—"}
    </Typography>

    <Select
      fullWidth
      value={dealForm.packageKey}
      onChange={(e) => {
        const newKey = e.target.value;
        const defaults = getPlanDefaults(newKey);
        setDealForm(prev => ({
          ...prev,
          packageKey: newKey,
          displayName: defaults?.displayName || prev.displayName,
          billingInterval: defaults?.billingInterval || prev.billingInterval,
          currency: defaults?.currency || prev.currency,
          seats: defaults?.seats ?? "",
          amount: defaults?.amount ?? "",
        }));
      }}
      sx={{ mb: 2 }}
    >
      <MenuItem value="school_50">School 50</MenuItem>
      <MenuItem value="school_100">School 100</MenuItem>
      <MenuItem value="school_200">School 200</MenuItem>
      <MenuItem value="school_250_plus">School 250+</MenuItem>
    </Select>

    <TextField
      label="Display Name"
      fullWidth
      value={dealForm.displayName}
      onChange={(e) => setDealForm({ ...dealForm, displayName: e.target.value })}
      sx={{ mb: 2 }}
    />

    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
      <TextField
        label="Seats"
        type="number"
        fullWidth
        value={dealForm.seats}
        onChange={(e) => setDealForm({ ...dealForm, seats: e.target.value })}
      />
      <TextField
        label="Amount (GBP)"
        type="number"
        fullWidth
        value={dealForm.amount}
        onChange={(e) => setDealForm({ ...dealForm, amount: e.target.value })}
      />
    </Stack>

    <TextField
      label="Notes (optional)"
      fullWidth
      multiline
      minRows={2}
      value={dealForm.notes}
      onChange={(e) => setDealForm({ ...dealForm, notes: e.target.value })}
      sx={{ mb: 2 }}
    />

    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        fullWidth
        disabled={dealLoading}
        onClick={saveDealToRequest}
      >
        Save Deal
      </Button>

      <Button
        variant="outlined"
        fullWidth
        onClick={() => setDealModalOpen(false)}
      >
        Cancel
      </Button>
    </Stack>
  </Box>
</Modal>


      {/* School Detail Modal */}
      <Modal open={schoolModalOpen} onClose={() => setSchoolModalOpen(false)}>
        <Box sx={{ backgroundColor: 'white', padding: 4, borderRadius: 2, maxWidth: 600, margin: '5% auto' }}>
          <Typography variant="h6" mb={2}>Users at {selectedSchool?.name}</Typography>
          <Tabs value={schoolSubTab} onChange={(e, val) => setSchoolSubTab(val)} sx={{ mb: 2 }}>
            <Tab label="Teachers" />
            <Tab label="Students" />
          </Tabs>

          {schoolSubTab === 0 && (
            <DataGrid
              rows={selectedTeachers}
              columns={[
                { field: 'firstName', headerName: 'First Name', flex: 1 },
                { field: 'lastName', headerName: 'Last Name', flex: 1 },
                { field: 'email', headerName: 'Email', flex: 1 },
              ]}
              autoHeight
              pageSizeOptions={[5, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 5, page: 0 } } }}
            />
          )}

          {schoolSubTab === 1 && (
            <DataGrid
              rows={selectedStudents}
              columns={[
                { field: 'firstName', headerName: 'First Name', flex: 1 },
                { field: 'lastName', headerName: 'Last Name', flex: 1 },
                { field: 'className', headerName: 'Class', width: 100 },
              ]}
              autoHeight
              pageSizeOptions={[5, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 5, page: 0 } } }}
            />
          )}
          <Stack direction="row" justifyContent="flex-end" mt={3}>
            <Button variant="outlined" onClick={() => setSchoolModalOpen(false)}>
              Close
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Add School Modal */}
      <Modal
  open={newSchoolModalOpen}
  onClose={() => {
    resetNewSchoolData();
    setNewSchoolModalOpen(false);
  }}
>

        <Box sx={{ ...modalStyle }}>
          <Typography variant="h6" mb={2}>Add School</Typography>

          <TextField
            label="School Name"
            fullWidth
            sx={{ mb: 2 }}
            value={newSchoolData.schoolName}
            onChange={(e) => setNewSchoolData({ ...newSchoolData, schoolName: e.target.value })}
          />
          <TextField
            label="Email"
            fullWidth
            sx={{ mb: 2 }}
            value={newSchoolData.email}
            onChange={(e) => setNewSchoolData({ ...newSchoolData, email: e.target.value })}
          />
          <TextField
            label="Country"
            fullWidth
            sx={{ mb: 2 }}
            value={newSchoolData.country}
            onChange={(e) => setNewSchoolData({ ...newSchoolData, country: e.target.value })}
          />
          <TextField
            label="Contact Full Name"
            fullWidth
            sx={{ mb: 2 }}
            value={newSchoolData.contactName}
            onChange={(e) => setNewSchoolData({ ...newSchoolData, contactName: e.target.value })}
          />
          <TextField
            label="Phone Number"
            fullWidth
            margin="dense"
            value={newSchoolData.phoneNumber}
            onChange={(e) => setNewSchoolData({ ...newSchoolData, phoneNumber: e.target.value })}
          />

          {/* ✅ Plan settings (silent invoice-style activation) */}
<Select
  fullWidth
  sx={{ mt: 2, mb: 2 }}
  value={newSchoolData.packageKey}
  onChange={(e) => {
    const packageKey = e.target.value;
    const defaults = getPlanDefaults(packageKey); // uses your INVOICE_PLAN_CATALOG
    setNewSchoolData((prev) => ({
      ...prev,
      packageKey,
      seats: defaults?.seats === "" ? "" : Number(defaults?.seats || prev.seats || 0),
      billingInterval: defaults?.billingInterval || prev.billingInterval || "annual",
    }));
  }}
>
  <MenuItem value="school_50">School 50</MenuItem>
  <MenuItem value="school_100">School 100</MenuItem>
  <MenuItem value="school_200">School 200</MenuItem>
  <MenuItem value="school_250_plus">School 250+</MenuItem>
</Select>

<Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
  <TextField
    label="Seats"
    type="number"
    fullWidth
    value={newSchoolData.seats}
    onChange={(e) =>
      setNewSchoolData({ ...newSchoolData, seats: e.target.value })
    }
  />

  <Select
    fullWidth
    value={newSchoolData.billingInterval}
    onChange={(e) =>
      setNewSchoolData({ ...newSchoolData, billingInterval: e.target.value })
    }
  >
    <MenuItem value="annual">Annual</MenuItem>
    <MenuItem value="monthly">Monthly</MenuItem>
  </Select>
</Stack>


          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              label="Password"
              fullWidth
              value={newSchoolData.password}
              onChange={(e) => setNewSchoolData({ ...newSchoolData, password: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                navigator.clipboard.writeText(newSchoolData.password);
                alert("Password copied to clipboard!");
              }}
            >
              Copy
            </Button>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={async () => {
  const {
    email,
    schoolName,
    contactName,
    country,
    phoneNumber,
    password,
    packageKey,
    billingInterval,
    seats,
  } = newSchoolData;

  if (!email || !schoolName || !contactName || !country || !password) {
    alert("Fill in all fields (including password).");
    return;
  }

  // seats must be a positive number
  const seatsNum = Number(seats || 0);
  if (!seatsNum || seatsNum <= 0) {
    alert("Seats must be a number greater than 0.");
    return;
  }

  try {
    // ✅ Calls admin function: creates user + sets password + stamps invoice subscription
    const out = await adminCreateSchoolSilent({
      email,
      schoolName,
      contactName,
      country,
      phoneNumber: phoneNumber || "",
      password,
      packageKey,
      billingInterval,
      seats: seatsNum,
      // optional: if you want to set renewalDate here later, add it to payload
      // renewalDate: newSchoolData.renewalDate || null
    });

    alert(
      `✅ School created (silent)\n\nEmail: ${email}\nPassword: ${password}\nUID: ${out.uid}`
    );

    resetNewSchoolData();
    setNewSchoolModalOpen(false);
    fetchData();
    fetchUsageStats();
  } catch (err) {
    console.error("Silent school create failed:", err);
    alert(err?.message || "Failed to create school.");
  }
}}

            >
              Save School
            </Button>

            <Button
  variant="outlined"
  fullWidth
  onClick={() => {
    resetNewSchoolData();
    setNewSchoolModalOpen(false);
  }}
>
  Cancel
</Button>

          </Stack>
        </Box>
      </Modal>

{/* Edit School Modal */}
<Modal
  open={schoolEditModalOpen}
  onClose={() => {
    setSchoolEditModalOpen(false);
    setSchoolEditData(null);
  }}
>
  <Box sx={{ ...modalStyle }}>
    <Typography variant="h6" mb={2}>
      Edit School
    </Typography>

    <TextField
      label="School Name"
      fullWidth
      sx={{ mb: 2 }}
      value={schoolEditData?.name || ""}
      onChange={(e) =>
        setSchoolEditData((prev) => ({
          ...prev,
          name: e.target.value,
        }))
      }
    />

    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        fullWidth
        onClick={handleSaveSchoolEdits}
        disabled={!schoolEditData?.name?.trim()}
      >
        Save
      </Button>
      <Button
        variant="outlined"
        fullWidth
        onClick={() => {
          setSchoolEditModalOpen(false);
          setSchoolEditData(null);
        }}
      >
        Cancel
      </Button>
    </Stack>
  </Box>
</Modal>

    </Box>


  );
};

export default AdminDashboard;

